import DOMPurify from 'dompurify';

/**
 * Sanitiza HTML para prevenir XSS attacks
 * @param {string} dirty - HTML não sanitizado
 * @param {object} config - Configuração do DOMPurify
 * @returns {string} HTML sanitizado
 */
export const sanitizeHtml = (dirty, config = {}) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  const defaultConfig = {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img', 'div', 'span',
      'table', 'thead', 'tbody', 'tr', 'td', 'th', 'blockquote',
      'code', 'pre', 'hr', 'video', 'iframe'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'target', 'rel', 'width', 'height', 'allowfullscreen',
      'frameborder', 'allow'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  };

  const mergedConfig = { ...defaultConfig, ...config };

  return DOMPurify.sanitize(dirty, mergedConfig);
};

/**
 * Sanitiza HTML removendo todos os scripts e tags perigosas
 * Configuração mais restritiva para conteúdo não confiável
 * @param {string} dirty - HTML não sanitizado
 * @returns {string} HTML sanitizado
 */
export const sanitizeHtmlStrict = (dirty) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover'],
  });
};

/**
 * Sanitiza URL para prevenir javascript: e data: URIs maliciosos
 * @param {string} url - URL para sanitizar
 * @returns {string} URL sanitizada ou string vazia se inválida
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Remove espaços em branco
  const trimmed = url.trim();

  // Bloqueia protocolos perigosos
  const dangerousProtocols = [
    'javascript:',
    'data:text/html',
    'vbscript:',
    'file:',
    'about:',
  ];

  const lowerUrl = trimmed.toLowerCase();
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn('Blocked dangerous URL protocol:', protocol);
      return '';
    }
  }

  // Permite apenas HTTP, HTTPS, mailto, tel e caminhos relativos
  const allowedProtocolRegex = /^(https?:|mailto:|tel:|\/|\.\/|\.\.\/)/i;
  if (!allowedProtocolRegex.test(trimmed)) {
    console.warn('URL does not match allowed protocols:', trimmed);
    return '';
  }

  return trimmed;
};

/**
 * Valida e sanitiza atributo src de imagem
 * @param {string} src - Fonte da imagem
 * @returns {string} Src sanitizado
 */
export const sanitizeImageSrc = (src) => {
  if (!src || typeof src !== 'string') {
    return '';
  }

  const trimmed = src.trim();

  // Permite data URIs apenas para imagens
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }

  // Usa sanitizeUrl para outros casos
  return sanitizeUrl(trimmed);
};

/**
 * Escape de HTML entities para prevenir XSS em texto puro
 * @param {string} text - Texto para escapar
 * @returns {string} Texto com entities escapadas
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitiza nome de arquivo para download
 * @param {string} filename - Nome do arquivo
 * @returns {string} Nome de arquivo sanitizado
 */
export const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'download';
  }

  // Remove path separators e caracteres perigosos
  return filename
    .replace(/[/\\]/g, '')
    .replace(/\.\./g, '')
    .replace(/[<>:"|?*\0]/g, '')
    .trim() || 'download';
};

/**
 * Valida se uma string é um ID válido do Facebook Pixel
 * @param {string} pixelId - ID do pixel
 * @returns {boolean} true se válido
 */
export const isValidPixelId = (pixelId) => {
  if (!pixelId || typeof pixelId !== 'string') {
    return false;
  }

  // Facebook Pixel IDs são números com 15-16 dígitos
  return /^\d{15,16}$/.test(pixelId.trim());
};

/**
 * Cria objeto de configuração sanitizado para DOMPurify
 * Útil para landing pages com conteúdo rico
 * @returns {object} Configuração do DOMPurify
 */
export const getLandingPageSanitizeConfig = () => ({
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'b', 'i', 'em', 'strong', 'u', 's', 'strike',
    'a', 'img', 'video', 'audio',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'div', 'span', 'section', 'article', 'header', 'footer',
    'blockquote', 'pre', 'code',
    'iframe' // Cuidado: apenas se necessário para embeds
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'id', 'style',
    'target', 'rel', 'width', 'height', 'controls', 'autoplay',
    'allowfullscreen', 'frameborder', 'allow',
    'data-*' // Atributos data personalizados
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Permite iframes apenas de domínios confiáveis
  ALLOWED_IFRAME_HOSTNAMES: [
    'www.youtube.com',
    'player.vimeo.com',
    'www.facebook.com',
    'www.instagram.com'
  ],
  KEEP_CONTENT: true,
  RETURN_TRUSTED_TYPE: false,
});

export default {
  sanitizeHtml,
  sanitizeHtmlStrict,
  sanitizeUrl,
  sanitizeImageSrc,
  escapeHtml,
  sanitizeFilename,
  isValidPixelId,
  getLandingPageSanitizeConfig
};
